/**
 * Frontend error types matching backend APIError responses.
 *
 * This module provides:
 * - Type-safe error codes matching backend ErrorCode enum
 * - Structured error response interfaces
 * - Type guards for error discrimination
 * - Domain-specific error types for different services
 *
 * Design invariants:
 * - Error codes must match backend exactly (stable API contract)
 * - All errors are discriminated unions (use error_code for type narrowing)
 * - Type guards enable safe error handling
 * - Error details are strongly typed per error code
 *
 * @example
 * ```typescript
 * import { APIErrorResponse, isTrainingError, TrainingErrorCode } from '@/types/errors'
 *
 * function handleError(error: APIErrorResponse) {
 *   if (isTrainingError(error)) {
 *     if (error.error_code === TrainingErrorCode.INSUFFICIENT_DATA) {
 *       // Type narrowed to InsufficientDataError
 *       console.log('Need more training data')
 *     }
 *   }
 * }
 * ```
 */

// ============================================================================
// Core API Error Response
// ============================================================================

/**
 * Standard API error response from backend.
 * All backend APIError exceptions are serialized to this format.
 */
export interface APIErrorResponse {
  error_code: string
  message: string
  context?: Record<string, unknown>
}

// ============================================================================
// Authentication Error Codes
// ============================================================================

export enum AuthErrorCode {
  INVALID_CREDENTIALS = 'AUTH_INVALID_CREDENTIALS',
  TOKEN_EXPIRED = 'AUTH_TOKEN_EXPIRED',
  TOKEN_INVALID = 'AUTH_TOKEN_INVALID',
  MISSING_CREDENTIALS = 'AUTH_MISSING_CREDENTIALS',
  ACCOUNT_DISABLED = 'AUTH_ACCOUNT_DISABLED',
  INSUFFICIENT_PERMISSIONS = 'AUTH_INSUFFICIENT_PERMISSIONS',
  API_KEY_INVALID = 'AUTH_API_KEY_INVALID',
  DEMO_TOKEN_INVALID = 'AUTH_DEMO_TOKEN_INVALID',
}

export interface AuthError extends APIErrorResponse {
  error_code: AuthErrorCode
}

// ============================================================================
// Validation Error Codes
// ============================================================================

export enum ValidationErrorCode {
  INVALID_INPUT = 'VALIDATION_INVALID_INPUT',
  MISSING_FIELD = 'VALIDATION_MISSING_FIELD',
  INVALID_FORMAT = 'VALIDATION_INVALID_FORMAT',
  INVALID_VALUE = 'VALIDATION_INVALID_VALUE',
  CONSTRAINT_VIOLATION = 'VALIDATION_CONSTRAINT_VIOLATION',
  FEATURE_MISMATCH = 'VALIDATION_FEATURE_MISMATCH',
  SCHEMA_ERROR = 'VALIDATION_SCHEMA_ERROR',
}

export interface ValidationError extends APIErrorResponse {
  error_code: ValidationErrorCode
}

// ============================================================================
// Resource Error Codes
// ============================================================================

export enum ResourceErrorCode {
  NOT_FOUND = 'RESOURCE_NOT_FOUND',
  ALREADY_EXISTS = 'RESOURCE_ALREADY_EXISTS',
  CONFLICT = 'RESOURCE_CONFLICT',
  GONE = 'RESOURCE_GONE',
  TENANT_NOT_FOUND = 'TENANT_NOT_FOUND',
  USER_NOT_FOUND = 'USER_NOT_FOUND',
  MODEL_NOT_FOUND = 'MODEL_NOT_FOUND',
  MODEL_VERSION_NOT_FOUND = 'MODEL_VERSION_NOT_FOUND',
  PREDICTION_NOT_FOUND = 'PREDICTION_NOT_FOUND',
  BATCH_NOT_FOUND = 'BATCH_NOT_FOUND',
  DATA_SOURCE_NOT_FOUND = 'DATA_SOURCE_NOT_FOUND',
  ENTITY_NOT_FOUND = 'ENTITY_NOT_FOUND',
  WEBHOOK_NOT_FOUND = 'WEBHOOK_NOT_FOUND',
  NOTIFICATION_NOT_FOUND = 'NOTIFICATION_NOT_FOUND',
  CONNECTOR_NOT_FOUND = 'CONNECTOR_NOT_FOUND',
}

export interface ResourceError extends APIErrorResponse {
  error_code: ResourceErrorCode
}

// ============================================================================
// Training Error Codes (matches backend TrainingErrorCode)
// ============================================================================

export enum TrainingErrorCode {
  FAILED = 'TRAINING_FAILED',
  INSUFFICIENT_DATA = 'TRAINING_INSUFFICIENT_DATA',
  INVALID_CONFIG = 'TRAINING_INVALID_CONFIG',
  IN_PROGRESS = 'TRAINING_IN_PROGRESS',
  CANCELLED = 'TRAINING_CANCELLED',
  TIMEOUT = 'TRAINING_TIMEOUT',
  DATA_ERROR = 'TRAINING_DATA_ERROR',
  FEATURE_ERROR = 'TRAINING_FEATURE_ERROR',
}

export interface TrainingError extends APIErrorResponse {
  error_code: TrainingErrorCode
}

// ============================================================================
// Prediction Error Codes (matches backend PredictionErrorCode)
// ============================================================================

export enum PredictionErrorCode {
  FAILED = 'PREDICTION_FAILED',
  MODEL_NOT_DEPLOYED = 'PREDICTION_MODEL_NOT_DEPLOYED',
  INVALID_FEATURES = 'PREDICTION_INVALID_FEATURES',
  MISSING_FEATURES = 'PREDICTION_MISSING_FEATURES',
  MODEL_LOAD_FAILED = 'PREDICTION_MODEL_LOAD_FAILED',
  TIMEOUT = 'PREDICTION_TIMEOUT',
  BATCH_FAILED = 'PREDICTION_BATCH_FAILED',
}

export interface PredictionError extends APIErrorResponse {
  error_code: PredictionErrorCode
}

// ============================================================================
// Data Source Error Codes (matches backend)
// ============================================================================

export enum DataSourceErrorCode {
  NOT_CONFIGURED = 'DATA_SOURCE_NOT_CONFIGURED',
  CONNECTION_FAILED = 'DATA_SOURCE_CONNECTION_FAILED',
  AUTH_FAILED = 'DATA_SOURCE_AUTH_FAILED',
  SYNC_FAILED = 'DATA_SOURCE_SYNC_FAILED',
  NO_DATA = 'DATA_SOURCE_NO_DATA',
  INVALID_CONFIG = 'DATA_SOURCE_INVALID_CONFIG',
  TIMEOUT = 'DATA_SOURCE_TIMEOUT',
  RATE_LIMITED = 'DATA_SOURCE_RATE_LIMITED',
  // Additional error codes from backend
  SYNC_IN_PROGRESS = 'DATA_SOURCE_SYNC_IN_PROGRESS',
  ERROR_STATE = 'DATA_SOURCE_ERROR_STATE',
  INVALID_FILE = 'DATA_SOURCE_INVALID_FILE',
  FILE_NOT_FOUND = 'DATA_SOURCE_FILE_NOT_FOUND',
  UNSUPPORTED_FORMAT = 'DATA_SOURCE_UNSUPPORTED_FORMAT',
  READ_FAILED = 'DATA_SOURCE_READ_FAILED',
  PREVIEW_FAILED = 'DATA_SOURCE_PREVIEW_FAILED',
}

export interface DataSourceError extends APIErrorResponse {
  error_code: DataSourceErrorCode
}

// ============================================================================
// Rate Limit Error Codes
// ============================================================================

export enum RateLimitErrorCode {
  EXCEEDED = 'RATE_LIMIT_EXCEEDED',
  QUOTA_EXCEEDED = 'RATE_LIMIT_QUOTA_EXCEEDED',
  PLAN_LIMIT = 'RATE_LIMIT_PLAN_LIMIT',
}

export interface RateLimitError extends APIErrorResponse {
  error_code: RateLimitErrorCode
}

// ============================================================================
// Plan Limit Error Codes
// ============================================================================

export enum PlanLimitErrorCode {
  EXCEEDED = 'PLAN_LIMIT_EXCEEDED',
}

export interface PlanLimitError extends APIErrorResponse {
  error_code: PlanLimitErrorCode
  context?: {
    resource?: string
    current_count?: number
    plan_limit?: number
    current_plan?: string
  }
}

// ============================================================================
// Internal Error Codes
// ============================================================================

export enum InternalErrorCode {
  ERROR = 'INTERNAL_ERROR',
  DATABASE_ERROR = 'INTERNAL_DATABASE_ERROR',
  STORAGE_ERROR = 'INTERNAL_STORAGE_ERROR',
  CACHE_ERROR = 'INTERNAL_CACHE_ERROR',
  QUEUE_ERROR = 'INTERNAL_QUEUE_ERROR',
  NETWORK_ERROR = 'INTERNAL_NETWORK_ERROR',
  TIMEOUT = 'INTERNAL_TIMEOUT',
}

export interface InternalError extends APIErrorResponse {
  error_code: InternalErrorCode
}

// ============================================================================
// Domain-Specific Error Types (from service-level error modules)
// ============================================================================

/**
 * Training-specific error codes (from ml/training/errors.py)
 * These are more granular than the API-level TRAINING_* codes
 */
export enum DetailedTrainingErrorCode {
  // Data validation
  MISSING_COLUMN = 'missing_column',
  WRONG_DATA_TYPE = 'wrong_data_type',
  INVALID_DATA_VALUES = 'invalid_data_values',
  EMPTY_DATASET = 'empty_dataset',
  MISSING_TARGET = 'missing_target',

  // Insufficient data
  TOO_FEW_SAMPLES = 'too_few_samples',
  TOO_FEW_CLASSES = 'too_few_classes',
  INSUFFICIENT_CLASS_SAMPLES = 'insufficient_class_samples',
  INSUFFICIENT_FEATURES = 'insufficient_features',

  // Model training
  TRAINING_FAILED = 'training_failed',
  CONVERGENCE_FAILED = 'convergence_failed',
  NUMERICAL_INSTABILITY = 'numerical_instability',
  OVERFITTING_DETECTED = 'overfitting_detected',

  // Feature pipeline
  FEATURE_TRANSFORM_FAILED = 'feature_transform_failed',
  FEATURE_EXTRACTION_FAILED = 'feature_extraction_failed',
  INVALID_FEATURE_SCHEMA = 'invalid_feature_schema',

  // Storage
  SAVE_FAILED = 'save_failed',
  LOAD_FAILED = 'load_failed',
  STORAGE_UNAVAILABLE = 'storage_unavailable',

  // Cross-validation
  CV_SPLIT_FAILED = 'cv_split_failed',
  CV_SCORING_FAILED = 'cv_scoring_failed',

  // Unknown
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * Prediction-specific error codes (from services/predictions/errors.py)
 */
export enum DetailedPredictionErrorCode {
  // Model errors
  MODEL_NOT_FOUND = 'model_not_found',
  MODEL_LOAD_FAILED = 'model_load_failed',
  MODEL_ARTIFACT_CORRUPTED = 'model_artifact_corrupted',
  MODEL_NOT_ACTIVE = 'model_not_active',

  // Feature errors
  MISSING_FEATURES = 'missing_features',
  INVALID_FEATURE_TYPE = 'invalid_feature_type',
  FEATURE_TRANSFORM_FAILED = 'feature_transform_failed',
  FEATURE_SCHEMA_MISMATCH = 'feature_schema_mismatch',

  // Inference errors
  INFERENCE_FAILED = 'inference_failed',
  PREDICTION_OUT_OF_RANGE = 'prediction_out_of_range',
  BATCH_TOO_LARGE = 'batch_too_large',

  // Explanation errors
  EXPLAINER_INIT_FAILED = 'explainer_init_failed',
  EXPLANATION_GENERATION_FAILED = 'explanation_generation_failed',
  SHAP_BACKGROUND_MISSING = 'shap_background_missing',

  // Resource errors
  CACHE_ERROR = 'cache_error',
  TIMEOUT = 'timeout',
  MEMORY_EXCEEDED = 'memory_exceeded',

  // Unknown
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * Sync-specific error codes (from services/sync/errors.py)
 */
export enum SyncErrorCode {
  // Connector errors
  CONNECTOR_NOT_FOUND = 'connector_not_found',
  CONNECTOR_INIT_FAILED = 'connector_init_failed',
  AUTHENTICATION_FAILED = 'authentication_failed',
  CONNECTION_TEST_FAILED = 'connection_test_failed',
  INVALID_CREDENTIALS = 'invalid_credentials',

  // Data fetch errors
  FETCH_FAILED = 'fetch_failed',
  ENTITY_TYPE_NOT_SUPPORTED = 'entity_type_not_supported',
  API_RATE_LIMIT = 'api_rate_limit',
  API_QUOTA_EXCEEDED = 'api_quota_exceeded',
  TIMEOUT = 'timeout',

  // Data storage errors
  STORAGE_FAILED = 'storage_failed',
  DUPLICATE_RECORD = 'duplicate_record',
  INVALID_RECORD_FORMAT = 'invalid_record_format',
  DATABASE_ERROR = 'database_error',

  // Sync job errors
  SYNC_JOB_NOT_FOUND = 'sync_job_not_found',
  SYNC_JOB_ALREADY_RUNNING = 'sync_job_already_running',
  SYNC_JOB_CANCELLED = 'sync_job_cancelled',

  // Data source errors
  DATA_SOURCE_NOT_FOUND = 'data_source_not_found',
  DATA_SOURCE_DISABLED = 'data_source_disabled',
  DATA_SOURCE_MISCONFIGURED = 'data_source_misconfigured',

  // Validation errors
  EMPTY_BATCH = 'empty_batch',
  INVALID_TIMESTAMP = 'invalid_timestamp',
  MISSING_ID_FIELD = 'missing_id_field',

  // Unknown
  UNKNOWN_ERROR = 'unknown_error',
}

/**
 * Webhook-specific error codes (from services/webhooks/errors.py)
 */
export enum WebhookErrorCode {
  // Configuration errors
  WEBHOOK_NOT_FOUND = 'webhook_not_found',
  WEBHOOK_DISABLED = 'webhook_disabled',
  INVALID_WEBHOOK_URL = 'invalid_webhook_url',
  MISSING_EVENT_TYPE = 'missing_event_type',

  // Payload errors
  INVALID_PAYLOAD = 'invalid_payload',
  PAYLOAD_TOO_LARGE = 'payload_too_large',
  SIGNATURE_GENERATION_FAILED = 'signature_generation_failed',

  // Delivery errors
  DELIVERY_FAILED = 'delivery_failed',
  HTTP_ERROR = 'http_error',
  NETWORK_ERROR = 'network_error',
  TIMEOUT = 'timeout',
  DNS_RESOLUTION_FAILED = 'dns_resolution_failed',
  SSL_ERROR = 'ssl_error',

  // Response errors
  INVALID_RESPONSE = 'invalid_response',
  HTTP_CLIENT_ERROR = 'http_client_error',
  HTTP_SERVER_ERROR = 'http_server_error',

  // Retry errors
  MAX_RETRIES_EXCEEDED = 'max_retries_exceeded',
  RETRY_BACKOFF_EXCEEDED = 'retry_backoff_exceeded',

  // Database errors
  DELIVERY_RECORD_NOT_FOUND = 'delivery_record_not_found',
  DATABASE_ERROR = 'database_error',

  // Platform webhook errors
  N8N_NOT_CONFIGURED = 'n8n_not_configured',
  N8N_DELIVERY_FAILED = 'n8n_delivery_failed',

  // Unknown
  UNKNOWN_ERROR = 'unknown_error',
}

// ============================================================================
// Union Type for All Errors
// ============================================================================

export type AnyAPIError =
  | AuthError
  | ValidationError
  | ResourceError
  | TrainingError
  | PredictionError
  | DataSourceError
  | RateLimitError
  | PlanLimitError
  | InternalError
  | APIErrorResponse

// ============================================================================
// HTTP Status to Error Category Mapping
// ============================================================================

// ErrorCategory and categorizeError have been moved to @/lib/error-utils
// for better bundling and to avoid Safari "Can't find variable" errors
